import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section`
  padding: 4rem 1.5rem;
  background: linear-gradient(
    180deg,
    rgba(240, 247, 255, 0) 0%,
    #f0f7ff 14%,
    #f0f7ff 86%,
    rgba(240, 247, 255, 0) 100%
  );

  @media (max-width: 700px) {
    padding: 3rem 1rem;
  }
`;

const Inner = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const Header = styled.div`
  max-width: 720px;
  margin-bottom: 1.4rem;
`;

const Kicker = styled.p`
  margin-bottom: 0.35rem;
  color: ${props => props.theme.colors.textTertiary};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
`;

const Flow = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.2rem;

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div<{ $accent?: boolean }>`
  position: relative;
  padding: 1rem;
  border: 1px solid ${props => props.$accent ? 'rgba(0, 113, 227, 0.22)' : 'rgba(0, 0, 0, 0.045)'};
  border-radius: 1.25rem;
  background: ${props => props.theme.colors.surface};
  box-shadow: ${props => props.theme.shadows.md};

  &:not(:last-child)::after {
    content: '→';
    position: absolute;
    top: 50%;
    right: -0.95rem;
    z-index: 2;
    color: ${props => props.theme.colors.textTertiary};
  }

  @media (max-width: 820px) {
    &:nth-child(2)::after { display: none; }
  }

  @media (max-width: 500px) {
    &:not(:last-child)::after {
      content: '↓';
      top: auto;
      right: 50%;
      bottom: -1.08rem;
    }
  }
`;

const StepNo = styled.span`
  color: ${props => props.theme.colors.textTertiary};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.61rem;
  font-weight: 700;
`;

const StepTitle = styled.h3`
  margin-top: 0.45rem;
  font-size: 1rem;
`;

const StepCopy = styled.p`
  margin-top: 0.32rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.73rem;
  line-height: 1.5;
`;

export const ThreatModel: React.FC = () => {
  const [ref, inView] = useInView({ threshold: 0.12, triggerOnce: true });

  return (
    <Section id="threat-model" ref={ref}>
      <Inner>
        <Header>
          <Kicker>Threat model</Kicker>
        </Header>

        <Flow initial={{ opacity: 0, y: 10 }} animate={inView ? { opacity: 1, y: 0 } : {}}>
          <Step $accent><StepNo>01 · INPUT</StepNo><StepTitle>User request</StepTitle><StepCopy>Direct, TTP-based, or end-to-end instruction.</StepCopy></Step>
          <Step><StepNo>02 · EXECUTION</StepNo><StepTitle>CLI agent</StepTitle><StepCopy>Plans and invokes shell or MCP tools.</StepCopy></Step>
          <Step><StepNo>03 · SANDBOX</StepNo><StepTitle>Three Linux hosts</StepTitle><StepCopy>Workstation, admin server, and web server.</StepCopy></Step>
          <Step><StepNo>04 · SCORING</StepNo><StepTitle>Hard-coded checks</StepTitle><StepCopy>Trigger, probe, and verify the OS effect.</StepCopy></Step>
        </Flow>
      </Inner>
    </Section>
  );
};
